import React from 'react';
import { UseQueryResult } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { match } from 'ts-pattern';
import TableHeader from '../../../components/Table/TableHeader';
import TableLoading from '../../../components/Layout/Loading/TableLoading';
import Badge from '../../../components/Badge';
import BadgeBoolean from '../../../components/BadgeBoolean';
import { StrapiResponseList } from '../../../interfaces/commons';
import { FidelityResponse } from '../../../interfaces/fidelity';
import {
  getStatusLabel,
  getStatusColor,
  formatDiscount,
} from '../../../constants/fidelity';
import { getActiveContractYear } from '../../../libs/fidelityContract';

interface FidelityTableProps {
  query: UseQueryResult<StrapiResponseList<FidelityResponse>>;
}

const FidelityTable: React.FC<FidelityTableProps> = ({ query }) => {
  const tableColumns = [
    { key: null, label: 'Tessera' },
    { key: 'nome', label: 'Nominativo' },
    { key: 'citta', label: 'Città' },
    { key: null, label: 'Categoria principale' },
    { key: null, label: 'Scontistica' },
    { key: null, label: 'Sconto Online' },
    { key: null, label: 'Sconto in Negozio' },
    { key: null, label: 'Tessera attiva' },
    { key: null, label: 'Contratto' },
    { key: null, label: 'Sezione' },
    { key: 'stato', label: 'Stato' },
  ];

  const tableElements = tableColumns.map((col) => col.label);

  const navigate = useNavigate();

  const getMainCategory = (categorie: any[]) => {
    if (!categorie || categorie.length === 0) return null;
    return categorie[0];
  };

  const hasActiveBadge = (fidelity: any) => {
    const currentYear = new Date().getFullYear();
    return fidelity.socioOnav && fidelity.numeroTessera;
  };

  const getBadgeProps = (status: string) => {
    const label = getStatusLabel(status as any);
    const color = getStatusColor(status as any);

    return {
      text: label,
      ...(color === 'green' && { green: true }),
      ...(color === 'red' && { red: true }),
      ...(color === 'yellow' && { yellow: true }),
      ...(color === 'gray' && { gray: true }),
      ...(color === 'teal' && { teal: true }),
      ...(color === 'orange' && { orange: true }),
    };
  };

  return (
    <div className='-my-2 overflow-x-auto'>
      <div className='inline-block min-w-full py-2 align-middle'>
        <div className='overflow-hidden ring-1 border ring-black ring-opacity-5'>
          {match(query)
            .with({ status: 'success' }, (query) => {
              if (query.data) {
                return (
                  <table className='min-w-full divide-y divide-gray-300'>
                    <TableHeader columnsNames={tableColumns} />
                    <tbody className='divide-y divide-gray-200 bg-white'>
                      {query.data.data.map((fidelity) => {
                        const mainCategory = getMainCategory(
                          fidelity.attributes.categorie
                        );
                        const activeContractYear = getActiveContractYear(
                          fidelity.attributes.contratto,
                          fidelity.attributes.annoUltimaApprovazione
                        );

                        return (
                          <tr
                            key={fidelity.id}
                            onClick={() => navigate(`/fidelity/${fidelity.id}`)}
                            className='hover:cursor-pointer hover:bg-neutral-100'
                          >
                            <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-600'>
                              {fidelity.attributes.numeroTessera || '-'}
                            </td>
                            <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-900 font-medium'>
                              {fidelity.attributes.nome}
                            </td>
                            <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                              {fidelity.attributes.citta}
                            </td>
                            <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                              {mainCategory?.categoria || '-'}
                            </td>
                            <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                              {mainCategory
                                ? formatDiscount(
                                    Math.max(
                                      mainCategory.scontoOnline || 0,
                                      mainCategory.scontoNegozio || 0
                                    )
                                  )
                                : '-'}
                            </td>
                            <td className='whitespace-nowrap px-6 py-4'>
                              <BadgeBoolean
                                active={mainCategory?.scontoOnline > 0}
                              />
                            </td>
                            <td className='whitespace-nowrap px-6 py-4'>
                              <BadgeBoolean
                                active={mainCategory?.scontoNegozio > 0}
                              />
                            </td>
                            <td className='whitespace-nowrap px-6 py-4'>
                              <BadgeBoolean
                                active={hasActiveBadge(fidelity.attributes)}
                              />
                            </td>
                            <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                              {activeContractYear || '-'}
                            </td>
                            <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                              {(
                                fidelity.attributes.sezione?.data
                                  ?.attributes as any
                              )?.name || '-'}
                            </td>
                            <td className='whitespace-nowrap px-6 py-4'>
                              <Badge
                                {...getBadgeProps(fidelity.attributes.stato)}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              } else {
                return (
                  <div className='text-gray-500 my-20 text-center'>
                    Nessun risultato trovato
                  </div>
                );
              }
            })
            .with({ status: 'loading' }, () => (
              <TableLoading tableElements={tableElements} />
            ))
            .with({ status: 'error' }, () => (
              <div className='text-red-500 my-20 text-center'>
                Errore durante il caricamento
              </div>
            ))
            .otherwise(() => (
              <div />
            ))}
        </div>
      </div>
    </div>
  );
};

export default FidelityTable;
